import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteNonNamedDealerResult,
  NonNamedDealer,
  NonNamedDealerApiItem,
  UpsertNonNamedDealerRequest,
} from './non-named-dealer.types';
import { apiUrl } from '../../../auth/api-url';
import { isConflictError, resolveApiErrorMessage } from '../../shared/api-error';

@Injectable({
  providedIn: 'root',
})
export class NonNamedDealerService {
  private readonly endpoint = apiUrl('nonnameddealer');

  constructor(private readonly http: HttpClient) {}

  getNonNamedDealers(): Observable<NonNamedDealer[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeNonNamedDealer(item))),
    );
  }

  createNonNamedDealer(payload: UpsertNonNamedDealerRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateNonNamedDealer(id: number, payload: UpsertNonNamedDealerRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteNonNamedDealer(id: number): Observable<DeleteNonNamedDealerResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteNonNamedDealerResult => ({ kind: 'success' })),
      catchError((error: unknown) => of<DeleteNonNamedDealerResult>({
        kind: 'error',
        message: isConflictError(error)
          ? resolveApiErrorMessage('This non named dealer is linked to trades and cannot be removed.', error)
          : 'Unable to delete non named dealer at this time. Please try again.',
      })),
    );
  }

  private normalizeNonNamedDealer(item: NonNamedDealerApiItem): NonNamedDealer {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: NonNamedDealerApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): NonNamedDealerApiItem[] {
    if (Array.isArray(response)) {
      return response as NonNamedDealerApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as NonNamedDealerApiItem[];
      }

      if (Array.isArray(items)) {
        return items as NonNamedDealerApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
